import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { MessageService } from '../services/message.service';
import { GlobalVariables } from '../common/global-variables';

const calendarUrl = GlobalVariables.calendarUrl;
const serviceidsUrl = GlobalVariables.serviceidsUrl;

const timetablesUrl = GlobalVariables.timetablesUrl;
const timetablesExtension = GlobalVariables.timetablesExtension;

const pdfScheduleUrl = GlobalVariables.pdfScheduleUrl;

const stopScheduleUrl = GlobalVariables.stopScheduleUrl;
const stopScheduleExtension = GlobalVariables.stopScheduleExtension;

const realtimeStatusBus = "https://www3.septa.org/api/TransitViewAll/"
const realtimeStatusRail = "https://www3.septa.org/api/TrainView/"

@Injectable({
  providedIn: 'root'
})
export class ScheduleService {

  public calendar: any;
  public route_service: any;
  public release_date: string = "";

  public rail_routes = GlobalVariables.railRoutes;

  constructor(private http: HttpClient, private messageService: MessageService) { 
    this.get_release_date();
  }

  public get_calendar(): Observable<any[]> {
    var data = this.http.get(calendarUrl)
      .pipe(
        map((array: any) => array = this.format_calendar(array)),
        tap(_ => this.log('fetched stop times')),
        catchError(this.handleError<any[]>('get', []))
      );

    return data;
  }

  public get_serivce(): Observable<object> {
    var data = forkJoin({
      calendar: this.http.get(calendarUrl),
      route_service: this.http.get(serviceidsUrl),
    });

    return data;
  }

  public get_release_date(): Observable<object> {
    var data = this.http.get(calendarUrl)
      .pipe(
        map((array: any) => array = this.format_release_date_from_calendar(array)),
        tap(_ => this.log('fetched stop times')),
        catchError(this.handleError<any[]>('get', []))
      );

    return data;
  }

  public get_release_date_(): string {
    var data = "";

       const calendar = this.get_calendar()
      .subscribe(calendar => {

        const selected_date = this.get_date();

        this.get_release_date = calendar[selected_date]['week_schedule']['Weekday'][0].split('_')[0];
        data = calendar[selected_date]['week_schedule']['Weekday'][0].split('_')[0];
      });

    return data;
  }

  public get_timetable(route_id: string, service_id: string, direction_id: string = "0"): Observable<boolean> {

    var url = timetablesUrl + "/" + service_id + "/" + route_id + "/" + direction_id + "/" + timetablesExtension;

    var data = this.http.get(url).pipe(
      map((array: any) => array = this.format_timetable(array)),
      tap(_ => this.log('fetched time tables')),
      catchError(this.handleError<any[]>('get', []))
    );

    return data;

  }

  public get_pdf_schedule(route_id: string = ""): Observable<boolean> {

    var url = pdfScheduleUrl;

    var data = this.http.get(url).pipe(
      map((array: any,) => array = this.format_pdf_schedule(array, route_id)),
      tap(_ => this.log('fetched time tables')),
      catchError(this.handleError<any[]>('get', []))
    );

    return data;

  }

  public get_stop_schedule_realtime(route_id: string, stop_id: string, service_id: string): Observable<boolean> {

    var url = stopScheduleUrl + route_id + "/" + stop_id + "/" + stopScheduleExtension;

    var data = this.http.get(url).pipe(
      map((array: any) => array = this.format_stop_schedule_realtime(array, route_id)),
      tap(_ => this.log('fetched time tables')),
      catchError(this.handleError<any[]>('get', []))
    );

    return data;

  }

  public get_stop_schedule(route_id: string, stop_id: string, service_id: string): Observable<boolean> {
    
    if (route_id != null && stop_id != null) {
      var url = stopScheduleUrl + route_id + "/" + stop_id + "/" + stopScheduleExtension;

      var data = this.http.get(url).pipe(
        map((array: any) => array = this.format_stop_schedule(array, service_id)),
        tap(_ => this.log('fetched time tables')),
        catchError(this.handleError<any[]>('get', []))
      );
    }

    return data;

  }

  public format_stop_schedule(response: any, service_id: any): any {

    var stop_times: any = []

    var entries = Object.entries(response);
    for (const stop_time in entries) {
      entries[stop_time][1]["actual_arrival_time"] = this.format_gtfs_actual_time(entries[stop_time][1]["arrival_time"]);
      entries[stop_time][1]["arrival_time"] = this.format_gtfs_time(entries[stop_time][1]["arrival_time"]);
      if (this.rail_routes.includes(entries[stop_time][1]["route_id"])) {
        entries[stop_time][1]["mode"] = "rail";
      }
      else {
        entries[stop_time][1]["mode"] = "bus";
      }

      stop_times.push(entries[stop_time][1]);
    }

    return stop_times;
  }

  public format_stop_schedule_realtime(response: any, route_id: any): any {

    var stop_times: any = []

    var entries = Object.entries(response);

    this.get_realtime_status(route_id).subscribe((data) => {
      stop_times = data;
    });

    for (const stop_time in entries) {
      entries[stop_time][1]["actual_arrival_time"] = this.format_gtfs_actual_time(entries[stop_time][1]["arrival_time"]);
      entries[stop_time][1]["arrival_time"] = this.format_gtfs_time(entries[stop_time][1]["arrival_time"]);



      stop_times.push(entries[stop_time][1]);
    }

    return stop_times;
  }

  public get_realtime_status(route_id: string): Observable<boolean> {

    var url: string = realtimeStatusBus;
    var mode: string = "bus";

    if (this.rail_routes.includes(route_id)) {
      mode = 'rail'
      url = realtimeStatusRail;
    }

    var data = this.http.jsonp(url, 'callback').pipe(
      map((array: any) => array = this.format_realtime_status(array, mode, route_id)),
      tap(_ => this.log('fetched time tables')),
      catchError(this.handleError<any[]>('get', []))
    );

    return data;

  }

  public format_realtime_status(response: any, mode: string, route_id: string = ""): any {

    var trips: any = []

    var entries = Object.entries(response);

    if (mode == 'bus' && route_id != "") {
      return entries[0][1][0][route_id];
    }

    if (mode == 'rail' && route_id != "") {
      for (const stop_time in entries) {
        trips.push(
          {
            "trip": entries[stop_time][1]["trainno"],
            "late": entries[stop_time][1]["late"],
            "track": entries[stop_time][1]["TRACK"],
            "track_change": entries[stop_time][1]["TRACK_CHANGE"],
            "lat": entries[stop_time][1]["lat"],
            "lng": entries[stop_time][1]["lon"],
            "current_stop_name": entries[stop_time][1]["currentstop"],
            "next_stop_name": entries[stop_time][1]["nextstop"],
            "type": entries[stop_time][1]["service"],
            "cars": entries[stop_time][1]["consist"],
          }
        );
      }
      return trips;
    }


    for (const stop_time in entries) {

      trips.push(entries[stop_time]);
    }

    return trips;
  }

  public format_timetable(response: any): any {

    var stops: any = [];
    var times: any = [];

    for (const stop in response) {

      console.log(Object.keys(response[stop]).length);

      if(Object.keys(response[stop]).length > 0){
        var stop_name = response[stop][Object.keys(response[stop])[0]]["stop_name"];
        stops.push(stop_name);
        times[stop_name] = []
      }

      console.log("hello");
      console.log(response[stop]);

      var entries = Object.entries(response[stop]);
      for (let key of entries) {
        if (key[1]["arrival_time"] != "---") {
          key[1]["arrival_time"] = this.format_gtfs_time(key[1]["arrival_time"]);
        }

        times[stop_name].push([key[1]["block_id"], key[1]["trip_id"], key[1]["arrival_time"]]);
      }

    }

    return [stops, times];
  }

  public format_pdf_schedule(response: any, route_id = ""): any {

    var schedules: any = [];
    for (const schedule in response) {
      if(response[schedule]["route_id"] == route_id || route_id == ""){
        schedules.push(response[schedule]);
      }
    }

    return schedules;
  }

  format_calendar(response: any): any {
    var formatted: any[] = [];

    for (const array of Object.entries(response)) {
      formatted.push(array);
    }

    return response;
  }

  format_release_date_from_calendar(response: any): any {
    var formatted: any[] = [];

    for (const array of Object.entries(response)) {
      const selected_date = this.get_date();
      if(array[0] == selected_date){
        formatted.push(array[1]['week_schedule']['Weekday'][0].split('_')[0]);
      }
    }

    return formatted[0];
  }

  private format_gtfs_time(time: any) {
    var hour: number = time.toString().slice(0, 2);
    var minute: string = time.toString().slice(3, 5);
    var period: string = ' am';
    if (hour >= 12) {
      period = ' pm';
    }

    if (hour > 23) {
      hour = hour - 24;
      if (hour < 12) {
        period = ' am';
      }
      if (hour == 0) {
        hour = 12;
      }
    }

    if (hour > 12 && hour < 24) {
      hour = hour - 12;
    }

    return hour + ':' + minute + period;
  }


  private format_gtfs_actual_time(time: any) {

    var current_date: Date = new Date();

    var hour: number = time.toString().slice(0, 2);
    var minute: string = time.toString().slice(3, 5);
    var period: string = ' am';

    if (hour <= 12) {
      current_date.setHours(hour);
    }

    if (hour >= 12) {
      period = ' pm';
    }

    if (hour > 23) {
      hour = hour - 24;
      if (hour < 12) {
        period = ' am';
      }
      if (hour == 0) {
        hour = 12;
      }

      current_date.setDate(current_date.getDate() + 1);
      current_date.setHours(hour);
    }

    if (hour > 12 && hour < 24) {
      current_date.setHours(hour);
      hour = hour - 12;
    }

    current_date.setMinutes(Number(minute));
    current_date.setSeconds(0);

    return current_date.getTime();
  }

  public format_status(delay: number): string {
    var status = delay == 0 ? GlobalVariables.onTimeStatus : delay > 0 ? delay + " " + GlobalVariables.lateStatus : Math.abs(delay) + ' ' + GlobalVariables.earlyStatus;

    // Canceled 
    status = delay == 999 ? GlobalVariables.canceledStatus : status;

    // Untracked
    status = delay == 998 ? GlobalVariables.untrackedStatus : status;

    return status;
  }

  public get_date(request_date: string = "Today") {
    let date: Date = new Date();

    if (request_date != "Today") {
      date.setDate(27)
      date.setMonth(0o5)
      date.setFullYear(2019)
    }

    return date.getFullYear() + ("0" + (date.getMonth() + 1)).slice(-2) + ("0" + (date.getDate())).slice(-2);
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log('${operation} failed: ${error.message}');

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a RouteService message with the MessageService */
  private log(message: string) {
    this.messageService.add(`RouteService: ${message}`);
  }

}
